import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import HistoryIcon from "@mui/icons-material/History"
import ReplayCircleFilledIcon from "@mui/icons-material/ReplayCircleFilled"
import { CircularProgress, Grid, IconButton } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"

import { PredictionReview } from "../../../../types/generated/api/PredictionReview"
import { withErrorHandler } from "../../../../utils/ErrorHandling"
import { useCreatePredictionReviewMutation } from "../../hooks/useCreatePredictionReviewMutation"
import type { ReviewActionsProps } from "../../interfaces"
import { HistoryLog } from "./HistoryLog"

/**
 * Inline review controls: mark as reviewed/revoked and view history.
 */
export const ReviewActions: React.FC<ReviewActionsProps> = ({ original }) => {
  const { t } = useTranslation("decisions", { keyPrefix: "projectDecisions" })

  const [isHovering, setIsHovering] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const createPredictionReviewMutation = useCreatePredictionReviewMutation({
    onError: withErrorHandler(() => setIsHovering(false)),
    onSuccess: () => setIsHovering(false),
  })

  return (
    <Grid container direction="row" display="flex" gap={1} wrap="nowrap">
      {/* Mark / revoke button */}
      <Grid item>
        {original?.review == null || original.review?.status === PredictionReview.status.REVOKED ? (
          <Button
            variant="secondary"
            onClick={() =>
              createPredictionReviewMutation.mutate({
                prediction_uuid: original.uuid,
                status: PredictionReview.status.REVIEWED,
              })
            }
            disabled={createPredictionReviewMutation.isLoading}
          >
            {t("markAsReviewed")}
          </Button>
        ) : (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            gap={0.5}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={() =>
              !createPredictionReviewMutation.isLoading &&
              createPredictionReviewMutation.mutate({
                prediction_uuid: original.uuid,
                status: PredictionReview.status.REVOKED,
              })
            }
          >
            <Grid item display="flex" alignItems="center">
              {createPredictionReviewMutation.isLoading ? (
                <CircularProgress size={14} style={{ color: "var(--blue-text-2)", marginRight: "10px" }} />
              ) : (
                <IconButton disabled={createPredictionReviewMutation.isLoading} style={{ padding: "2px" }}>
                  {isHovering ? (
                    <ReplayCircleFilledIcon
                      fontSize="small"
                      className="base-table-icon"
                      style={{ fontSize: "1.25rem" }}
                    />
                  ) : (
                    <CheckCircleIcon fontSize="small" className="good-table-icon" style={{ fontSize: "1.25rem" }} />
                  )}
                </IconButton>
              )}
            </Grid>
            <Grid item display="flex" alignItems="center" style={{ fontSize: "0.9rem" }}>
              {original.review?.created_by}
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* History popover trigger */}
      <Grid item display="flex" alignItems="center">
        <Button variant="ghost" onClick={(e) => setAnchorEl(e?.target as HTMLButtonElement | null)}>
          <HistoryIcon fontSize="small" />
        </Button>
      </Grid>

      {/* Shared HistoryLog component */}
      <HistoryLog
        id="review-history"
        isOpen={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        predictionId={original.uuid}
        variant="review"
        enabled={Boolean(anchorEl)}
      />
    </Grid>
  )
}
